import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Box,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
} from '@mui/material';
import { Edit, Delete, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Client } from '../types/entities';

type SortField = 'name' | 'phone_number';
type SortDirection = 'asc' | 'desc' | 'default';

interface ClientTableProps {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
  onInfo: (client: Client) => void;
}

const ClientTable: React.FC<ClientTableProps> = ({ clients, onEdit, onDelete, onInfo }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('default');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: default -> asc -> desc -> default
      if (sortDirection === 'default') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('default');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    if (sortDirection === 'default' || !sortField) {
      return 0; // No sorting, maintain original order
    }
    
    // Handle null values - treat null as empty string for sorting
    const aValue = (a[sortField] || '').toLowerCase();
    const bValue = (b[sortField] || '').toLowerCase();
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });
  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedClients.map((client) => (
          <Card key={client.id} sx={{ width: '100%' }}>
            <CardContent sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {client.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    color="info"
                    onClick={() => onInfo(client)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(client)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => onDelete(client)}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              {client.phone_number && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  📞 {client.phone_number}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
          },
        },
      }}
    >
      <Table sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={sortField === 'name'}
                direction={sortField === 'name' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('name')}
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              >
                {t('common.name')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={sortField === 'phone_number'}
                direction={sortField === 'phone_number' && sortDirection !== 'default' ? sortDirection : undefined}
                onClick={() => handleSort('phone_number')}
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              >
                {t('common.phone')}
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedClients.map((client) => (
            <TableRow key={client.id}>
              <TableCell sx={{ fontSize: '1rem' }}>
                {client.name}
              </TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>
                {client.phone_number || '-'}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    color="info"
                    onClick={() => onInfo(client)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Info />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(client)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => onDelete(client)}
                    size="medium"
                    sx={{ 
                      padding: '8px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem'
                      }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ClientTable;
